import { useRef, useState } from 'react';

interface ImageUploaderProps {
  value: string;
  onChange: (url: string) => void;
  label?: string;
  aspectClass?: string;
  placeholder?: string;
}

export default function ImageUploader({
  value,
  onChange,
  label = '图片',
  aspectClass = 'aspect-video',
  placeholder = '点击上传本地图片或粘贴图片 URL',
}: ImageUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [urlInput, setUrlInput] = useState('');
  const [mode, setMode] = useState<'preview' | 'url'>('preview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 上传图片到服务器
  const uploadToServer = async (file: File): Promise<string | null> => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/upload/cover', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '上传失败');
      }

      const data = await response.json();
      return data.data.url;
    } catch (err) {
      console.error('Upload failed:', err);
      throw err;
    }
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('请选择图片文件');
      return;
    }
    
    // 验证文件大小 (10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('图片文件过大，请选择小于 10MB 的文件');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const url = await uploadToServer(file);
      if (url) {
        onChange(url);
        setMode('preview');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '上传失败，请重试');
    } finally {
      setLoading(false);
    }

    // Reset input
    e.target.value = '';
  };

  const handleUrlConfirm = () => {
    if (urlInput.trim()) {
      // 检测 base64 数据
      if (urlInput.trim().startsWith('data:image')) {
        setError('不支持 base64 格式，请使用图片上传功能或输入图片 URL');
        return;
      }
      onChange(urlInput.trim());
      setUrlInput('');
      setMode('preview');
      setError('');
    }
  };

  const handleUrlKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleUrlConfirm();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file || !file.type.startsWith('image/')) {
      setError('请拖拽图片文件');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('图片文件过大，请选择小于 10MB 的文件');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const url = await uploadToServer(file);
      if (url) {
        onChange(url);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '上传失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => e.preventDefault();

  return (
    <div>
      <label className="block text-xs font-medium text-[#374151] mb-1.5">{label}</label>

      {/* Error message */}
      {error && (
        <div className="mb-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">
          {error}
        </div>
      )}

      {/* Preview area */}
      {value && mode === 'preview' ? (
        <div className="relative group rounded-xl overflow-hidden border border-[#E5E7EB] bg-[#F9FAFB]">
          <div className={`w-full ${aspectClass}`}>
            <img src={value} alt="预览" className="w-full h-full object-cover" />
          </div>
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="px-3 py-1.5 bg-white text-[#374151] text-xs font-medium rounded-lg cursor-pointer whitespace-nowrap hover:bg-[#F3F4F6] transition-colors"
              >
                <i className="ri-upload-line mr-1" />
                换图
              </button>
              <button
                type="button"
                onClick={() => { setMode('url'); setUrlInput(''); }}
                className="px-3 py-1.5 bg-white text-[#374151] text-xs font-medium rounded-lg cursor-pointer whitespace-nowrap hover:bg-[#F3F4F6] transition-colors"
              >
                <i className="ri-links-line mr-1" />
                换URL
              </button>
              <button
                type="button"
                onClick={() => { onChange(''); setError(''); }}
                className="px-3 py-1.5 bg-red-500 text-white text-xs font-medium rounded-lg cursor-pointer whitespace-nowrap hover:bg-red-600 transition-colors"
              >
                <i className="ri-delete-bin-line mr-1" />
                删除
              </button>
            </div>
          </div>
          {loading && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <div className="text-white text-sm">上传中...</div>
            </div>
          )}
        </div>
      ) : (
        /* Upload area */
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className="border-2 border-dashed border-[#D1D5DB] rounded-xl bg-[#F9FAFB] hover:bg-[#F3F4F6] transition-colors cursor-pointer"
        >
          {loading ? (
            <div className={`w-full ${aspectClass} flex items-center justify-center`}>
              <div className="text-center">
                <div className="animate-spin w-8 h-8 border-2 border-[#3B82F6] border-t-transparent rounded-full mx-auto mb-2"></div>
                <div className="text-sm text-[#6B7280]">上传中...</div>
              </div>
            </div>
          ) : mode === 'url' ? (
            /* URL input mode */
            <div className={`w-full ${aspectClass} p-4 flex flex-col`}>
              <div className="flex-1 flex items-center justify-center">
                <div className="w-full max-w-md">
                  <input
                    type="url"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    onKeyDown={handleUrlKeyDown}
                    placeholder="粘贴图片 URL，例如: https://example.com/image.jpg"
                    className="w-full px-3 py-2 border border-[#D1D5DB] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent"
                  />
                  <div className="flex gap-2 mt-3">
                    <button
                      type="button"
                      onClick={handleUrlConfirm}
                      className="flex-1 px-4 py-2 bg-[#3B82F6] text-white text-sm font-medium rounded-lg hover:bg-[#2563EB] transition-colors"
                    >
                      确认
                    </button>
                    <button
                      type="button"
                      onClick={() => setMode('preview')}
                      className="flex-1 px-4 py-2 bg-[#F3F4F6] text-[#374151] text-sm font-medium rounded-lg hover:bg-[#E5E7EB] transition-colors"
                    >
                      取消
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Default upload mode */
            <div
              onClick={() => fileInputRef.current?.click()}
              className={`w-full ${aspectClass} flex flex-col items-center justify-center p-4`}
            >
              <i className="ri-upload-cloud-2-line text-4xl text-[#9CA3AF] mb-2"></i>
              <div className="text-sm text-[#6B7280] mb-1">{placeholder}</div>
              <div className="text-xs text-[#9CA3AF]">支持 JPG、PNG、GIF、WebP、SVG 格式，最大 10MB</div>
            </div>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFile}
        className="hidden"
      />
    </div>
  );
}
